import { useState, useRef, useCallback, useEffect, Dispatch, SetStateAction } from 'react';
import { chatRepository } from '../../../services/repositories/ChatRepository';
import { ChatMessage } from './types';
import { createModuleLogger } from '../../../utils/logger';

const log = createModuleLogger('[ChatPagination]');
const MESSAGES_PER_PAGE = 80;

export function useChatPagination(conversationDbId: string | null, currentOrganizationId?: string) {
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const isLoadingOlderRef = useRef(false);
    const scope = `${currentOrganizationId}:${conversationDbId}`;
    const scopeRef = useRef(scope);
    scopeRef.current = scope;

    const resetPagination = useCallback(() => {
        setHasMoreMessages(true);
        isLoadingOlderRef.current = false;
        setIsLoadingOlder(false);
    }, []);

    useEffect(() => { resetPagination(); }, [scope, resetPagination]);

    // Debounce: mínimo 400ms entre carregamentos
    const lastLoadTimeRef = useRef(0);

    const loadOlderMessages = useCallback(async (
        currentMessages: ChatMessage[],
        setCurrentMessages: Dispatch<SetStateAction<ChatMessage[]>>
    ): Promise<boolean> => {
        if (!conversationDbId || !hasMoreMessages || isLoadingOlderRef.current) {
            return false;
        }
        
        if (currentMessages.length === 0) {
            return false;
        }

        // Debounce
        const now = Date.now();
        if (now - lastLoadTimeRef.current < 400) return false;
        lastLoadTimeRef.current = now;

        const oldestMessage = currentMessages[0];
        if (!oldestMessage) return false;

        isLoadingOlderRef.current = true;
        setIsLoadingOlder(true);

        try {
            const olderMessages = await chatRepository.getOlderMessages(
                conversationDbId,
                oldestMessage.timestamp,
                currentOrganizationId,
                MESSAGES_PER_PAGE
            );

            if (scopeRef.current !== scope) return false;

            if (olderMessages.length < MESSAGES_PER_PAGE) {
                setHasMoreMessages(false);
            }

            if (olderMessages.length > 0) {
                setCurrentMessages(prev => {
                    const existingIds = new Set(prev.map(m => m.id));
                    const newMessages = olderMessages.filter(m => !existingIds.has(m.id));
                    
                    return [...newMessages, ...prev]
                        .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
                });
                return true;
            }

            return false;
        } catch (error) {
            log.error('❌ Erro ao carregar mensagens antigas:', error);
            return false;
        } finally {
            if (scopeRef.current === scope) {
                setIsLoadingOlder(false);
                isLoadingOlderRef.current = false;
            }
        }
    }, [conversationDbId, hasMoreMessages, currentOrganizationId, scope]);

    return {
        loadOlderMessages,
        isLoadingOlder,
        hasMoreMessages,
        resetPagination
    };
}
